import { colord } from "colord";
import { ALPHA } from "@showitinc/color-picker";
import {
  BUTTON_BACKGROUND_COLOR,
  BORDER_WIDTH,
  BORDER_RADIUS,
  TEXT_SIZE,
  TEXT_LETTER_SPACING,
  TEXT_LETTER_CASE,
  TEXT_LINE_HEIGHT,
  OPACITY,
  BLUR,
  SHADOW_STYLE,
  BUTTON_GRADIENT_COLOR_1,
  BUTTON_GRADIENT_COLOR_2,
  BUTTON_GRADIENT_ROTATION,
  ELEMENT_BACKGROUND_COLOR,
  ELEMENT_BACKGROUND_FILL_TYPE,
  GRADIENT_COLOR_1,
  GRADIENT_COLOR_2,
  GRADIENT_ROTATION,
  BORDER_COLOR,
  PADDING,
  TEXT_COLOR,
  TEXT_FONT,
  SHADOW_OFFSET_X,
  SHADOW_COLOR,
  SHADOW_OFFSET_Y,
  SHADOW_BLUR,
} from "@/constants/MergeConstants";
import AppConstants from "@/constants/AppConstants";
import { ON_SI_CHANGE, ALL_HANDLERS, PREVIEWABLE_HANDLERS } from "./ElementInputConstants.mjs";
import { LETTER_CASE_OPTIONS } from "./TextPropertyMaps";
import { getProp } from "../Merge/MergeUtils.mjs";

const { TRANSPARENT } = AppConstants;

/**
 * @typedef {import("immutable").Map} Map
 *
 * @typedef {Object} UpdateCallbackProps
 * @prop {CustomEvent} e
 * @prop {import("@/vo/BlockUpdate.mjs").PropUpdate} propInfo
 * @prop {Map} theme
 * @prop {Map} element
 */

/**
 * todo: probably pull this from that other file
 * @param {UpdateCallbackProps} props
 * @returns
 */
const handleColorChange = ({ e, propInfo }) => {
  if (!e?.detail) return { ...propInfo, value: e };
  const { origValue } = propInfo;
  const { changed, color, alpha } = e.detail;
  if (changed === ALPHA) {
    const [colorPart] = String(origValue || TRANSPARENT).split(":");
    return { ...propInfo, value: Number(alpha) === 100 ? colorPart : `${colorPart}:${alpha}` };
  }
  const { r, g, b } = color.rgba;
  return { ...propInfo, value: `${colord({ r, g, b }).toHex()}:${alpha}` };
};

/**
 * @typedef {Object} BaseInputMeta -
 * @param {string} type - Configures input type
 * @param {string} label - Input label
 * @param {Array<string>} inputEventHandlers - Named handlers to apply to the input
 *
 * @typedef {Object} NumberInputMeta
 *
 * @typedef {Object} TextInputMeta
 *
 */

export const BASE_INPUT_CONFIG = {
  [ELEMENT_BACKGROUND_FILL_TYPE]: {
    children: [
      BUTTON_BACKGROUND_COLOR,
      BUTTON_GRADIENT_COLOR_1,
      BUTTON_GRADIENT_COLOR_2,
      BUTTON_GRADIENT_ROTATION,
    ],
    inputEventHandlers: [ON_SI_CHANGE],
    label: "Fill type",
    shift: "left",
    type: "fill",
  },
  [ELEMENT_BACKGROUND_COLOR]: {
    context: false,
    inputEventHandlers: [...ALL_HANDLERS],
    label: "BG Color",
    type: "color",
    updateCallback: handleColorChange,
  },
  [GRADIENT_COLOR_1]: {
    context: false,
    inputEventHandlers: [...ALL_HANDLERS],
    label: "Color Start",
    type: "color",
    updateCallback: handleColorChange,
  },
  [GRADIENT_COLOR_2]: {
    context: false,
    inputEventHandlers: [...ALL_HANDLERS],
    label: "Color End",
    type: "color",
    updateCallback: handleColorChange,
  },
  [GRADIENT_ROTATION]: {
    context: false,
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Rotation",
    min: 0,
    max: 360,
    cycle: true,
    type: "number",
    units: "°",
  },
  [BORDER_COLOR]: {
    inputEventHandlers: [...ALL_HANDLERS],
    label: "Border Color",
    type: "color",
    updateCallback: handleColorChange,
  },
  [BORDER_WIDTH]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Border Width",
    min: 0,
    type: "number",
    units: "px",
    // updateCallback: handleMinimumSizeChange,
  },
  [BORDER_RADIUS]: {
    inputEventHandlers: [...ALL_HANDLERS],
    label: "Border Radius",
    min: 0,
    max: 100,
    type: "borderRadius",
    units: "px",
    usePresets: true,
    sideEffects: {
      length: (element, layout) => {
        const height = getProp(element, layout, "h");
        const width = getProp(element, layout, "w");

        return height < width ? height : width;
      },
    },
  },
  [PADDING]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Padding",
    min: 0,
    type: "padding",
    units: "px",
  },
  [OPACITY]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Opacity",
    min: 0,
    max: 100,
    type: "number",
    step: 10,
    units: "%",
  },
  [BLUR]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Blur",
    min: 0,
    type: "number",
    step: 1,
    units: "px",
  },
  [TEXT_COLOR]: {
    inputEventHandlers: [...ALL_HANDLERS],
    type: "color",
    label: "Text Color",
    updateCallback: handleColorChange,
  },
  [TEXT_FONT]: {
    inputEventHandlers: [ON_SI_CHANGE],
    type: "font",
    label: "Font",
  },
  [TEXT_SIZE]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Text Size",
    min: 0,
    type: "number",
    units: "px",
  },
  [TEXT_LETTER_SPACING]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Letter Spacing",
    min: 0,
    precision: 3,
    step: 0.1,
    type: "number",
    units: "em",
  },
  [TEXT_LETTER_CASE]: {
    inputEventHandlers: [ON_SI_CHANGE],
    label: "Letter Case",
    options: LETTER_CASE_OPTIONS,
    type: "select",
  },
  [TEXT_LINE_HEIGHT]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Line Height",
    min: 1,
    precision: 3,
    step: 0.1,
    type: "number",
    units: "u",
  },
  [SHADOW_STYLE]: {
    children: [SHADOW_COLOR, SHADOW_OFFSET_X, SHADOW_OFFSET_Y, SHADOW_BLUR],
    inputEventHandlers: [ON_SI_CHANGE],
    label: "Drop Shadow",
    type: "shadow",
  },
  [SHADOW_COLOR]: {
    context: false,
    inputEventHandlers: [...ALL_HANDLERS],
    label: "Color",
    type: "color",
    updateCallback: handleColorChange,
  },
  [SHADOW_OFFSET_X]: {
    context: false,
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "X-Offset",
    min: -100,
    max: 100,
    type: "number",
    units: "px",
  },
  [SHADOW_OFFSET_Y]: {
    context: false,
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Y-Offset",
    min: -100,
    max: 100,
    type: "number",
    units: "px",
  },
  [SHADOW_BLUR]: {
    context: false,
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Blur",
    min: 0,
    type: "number",
    units: "px",
  },
};

/**
 * Extracts spreadable props from BASE_INPUT_CONFIG.
 * Filters out non-prop fields and returns only Input-compatible props.
 * @param {string} configKey - Key from BASE_INPUT_CONFIG (e.g., OPACITY, BLUR)
 * @returns {Object} Props suitable for spreading onto an Input component
 */
export const getInputDefaults = (configKey) => {
  const config = BASE_INPUT_CONFIG[configKey];
  if (!config) return {};

  const { label, min, max, step, units, shift } = config;

  return {
    ...(label !== undefined && { label }),
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    ...(step !== undefined && { step }),
    ...(units !== undefined && { units }),
    ...(shift !== undefined && { shift }),
  };
};

/**
 * Maps simplified handler prop names to internal SI handler names.
 * @param {Object} handlers - Object with simplified handler names
 * @param {Function} [handlers.onBeforeChange] - Maps to onSIBeforeChange
 * @param {Function} [handlers.onInput] - Maps to onSIInput
 * @param {Function} [handlers.onSelect] - Maps to onSISelect
 * @param {Function} [handlers.onChange] - Maps to onSIChange
 * @returns {Object} Object with SI handler prop names
 */
export const mapHandlers = ({ onBeforeChange, onInput, onSelect, onChange } = {}) => ({
  ...(onBeforeChange && { onSIBeforeChange: onBeforeChange }),
  ...(onInput && { onSIInput: onInput }),
  ...(onSelect && { onSISelect: onSelect }),
  ...(onChange && { onSIChange: onChange }),
});
